import { LoaderCircleIcon, SearchIcon, SquareArrowOutUpRightIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'

import { Autocomplete, TextInput } from 'slab'
import { type PayrollProviderConfig } from 'utils/web_api.schemas'

const BeginTypingContent = () => (
  <div className='flex items-center justify-center p-10 body text-gray-500'>
    <SearchIcon className='w-4 h-4 text-thatch-500 mr-2 shrink-0' />
    Begin typing to search for your payroll provider
  </div>
)

const NoSearchResultsContent = ({ searchQuery, onClearSearch }: { searchQuery: string; onClearSearch: () => void }) => (
  <div className='flex flex-col items-center justify-center p-10'>
    <p className='text-sm text-gray-500'>
      No payroll providers match your search for "<span className='font-bold italic'>{searchQuery}</span>"
    </p>
    <span
      className='text-sm text-thatch-purple-primary hover:underline cursor-pointer'
      onClick={onClearSearch}
    >
      Clear search
    </span>
  </div>
)

interface SearchResultsContentProps {
  filteredPayrollProviderConfigs: PayrollProviderConfig[];
  searchQuery: string;
  onOpen: (payrollProviderConfig: PayrollProviderConfig) => Promise<void>;
}

const SearchResultsContent: React.FC<SearchResultsContentProps> = ({
  filteredPayrollProviderConfigs,
  searchQuery,
  onOpen,
}) => {
  const [selectedPayrollProviderConfigId, setSelectedPayrollProviderConfigId] =
    useState<string | undefined>(undefined)

  return filteredPayrollProviderConfigs.map((payrollProviderConfig) => {
    let matchingSearchStartIndex = payrollProviderConfig.name.toLowerCase().indexOf(searchQuery.trim().toLowerCase())
    let matchingSearchEndIndex = matchingSearchStartIndex + searchQuery.trim().length

    if (matchingSearchStartIndex === -1) {
      // if the search query is not found in the name, try to find it in the name without spaces
      // add back the spaces to the matchingSearchEndIndex to account for the spaces in the name when highlighting the search query in the provider name
      matchingSearchStartIndex = payrollProviderConfig.name.toLowerCase().replaceAll(' ', '').indexOf(searchQuery.trim().toLowerCase().replaceAll(' ', ''))
      matchingSearchEndIndex = matchingSearchStartIndex + searchQuery.trim().length + payrollProviderConfig.name.split(' ').length - 1
    }

    function renderName () {
      if (matchingSearchStartIndex === -1) {
        return payrollProviderConfig.name
      }

      return (
        <>
          {payrollProviderConfig.name.slice(0, matchingSearchStartIndex)}
          <span className='font-semibold bg-yellow-200'>
            {payrollProviderConfig.name.slice(
              matchingSearchStartIndex,
              matchingSearchEndIndex
            )}
          </span>
          {payrollProviderConfig.name.slice(matchingSearchEndIndex)}
        </>
      )
    }

    return (
      <Autocomplete.Option
        key={payrollProviderConfig.id}
        data-testid={`payroll-provider-search-result-${payrollProviderConfig.id}`}
        onSelect={async () => {
          setSelectedPayrollProviderConfigId(payrollProviderConfig.id)
          await onOpen(payrollProviderConfig)
        }}
        value=''
      >
        <div className='flex items-center'>
          <img
            aria-hidden // The image is decorative and not necessary for screen readers
            src={payrollProviderConfig.icon_image_url}
            className='w-8 h-8 rounded-full border border-gray-200 mr-4'
          />
          <div>{renderName()}</div>
          {selectedPayrollProviderConfigId === payrollProviderConfig.id
            ? (
              <LoaderCircleIcon size={16} className='ml-2 text-gray-500 animate-spin' />
              )
            : (
              <SquareArrowOutUpRightIcon size={16} className='ml-2 text-gray-500' />
              )}
        </div>
      </Autocomplete.Option>
    )
  })
}

interface PayrollProviderSearchProps {
  payrollProviders: PayrollProviderConfig[];
  onOpen: (payrollProviderConfig: PayrollProviderConfig) => Promise<void>;
}

export const PayrollProviderSearch = React.forwardRef<HTMLInputElement, PayrollProviderSearchProps>(
  ({ payrollProviders, onOpen }, ref) => {
    const [searchQuery, setSearchQuery] = useState<string>('')

    const filteredPayrollProviderConfigs = useMemo(
      () =>
        payrollProviders
          .filter(
            (payrollProvider) => {
              const name = payrollProvider.name.toLowerCase()
              const query = searchQuery.trim().toLowerCase()
              const remoteId = payrollProvider.api_connection_remote_id.toLowerCase()

              return (
                name.includes(query) ||
                name.replaceAll(' ', '').includes(query.replaceAll(' ', '')) ||
                remoteId.includes(query)
              )
            }
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      [payrollProviders, searchQuery]
    )

    function renderSearchResults () {
      if (searchQuery.trim().length === 0) {
        return <BeginTypingContent />
      }

      if (filteredPayrollProviderConfigs.length === 0) {
        return (
          <NoSearchResultsContent
            searchQuery={searchQuery}
            onClearSearch={() => setSearchQuery('')}
          />
        )
      }

      return (
        <SearchResultsContent
          filteredPayrollProviderConfigs={filteredPayrollProviderConfigs}
          searchQuery={searchQuery}
          onOpen={onOpen}
        />
      )
    }

    return (
      <Autocomplete>
        <Autocomplete.Input asChild openOnFocus>
          <TextInput
            data-testid='payroll-provider-search-input'
            id='payroll-provider-search'
            label='Search for your payroll provider'
            placeholder='Payroll providers will display as you type'
            ref={ref}
            onChange={(e) => {
              setSearchQuery(e.target.value)
            }}
            value={searchQuery}
          />
        </Autocomplete.Input>
        <Autocomplete.List>{renderSearchResults()}</Autocomplete.List>
      </Autocomplete>
    )
  }
)
